import { type ReactElement } from 'react';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { AdHocAggregateResult } from '@/shared/analytics/AnalyticsTypes';
import RankedList, { type RankedListColumn, type RankedListPrimary } from '@/design/RankedList/RankedList';
import { sliceToPath } from './TopicSliceKey';
import { fieldCarriesConfidence, formatCount, formatPercent, humanUser, metricLabel, peakShareFill, share } from '@/shared/analytics/MetricFormatting';

const METRIC_WIDTH = 72;
const REACH_WIDTH = 54;

interface RankedListVizProps {
    definition: TopicDefinition;
    result: AdHocAggregateResult;
    basePath: string;
}

interface RankedRow {
    value: string;
    rawKey: string;
    metricValue: number;
    users: number;
    confidence: number;
    share: number;
    widthPct: number;
}

/**
 * The ranked breakdown as a real table — the shared {@link RankedList} driven by
 * a generic topic aggregate. Each row links into its slice (people breakdown
 * when the topic has a `drillField`, otherwise straight to evidence). People and
 * confidence columns appear only where they carry meaning for the topic.
 */
export default function RankedListViz({ definition, result, basePath }: RankedListVizProps): ReactElement {
    const metric = definition.headlineMetric;
    const total = result.totals[metric];
    const max = result.rows.reduce((peak, row) => Math.max(peak, row.metrics[metric]), 0);
    const showPeople = metric !== 'users' && definition.primaryField !== 'user_id';
    const showConfidence = fieldCarriesConfidence(definition.primaryField);
    const lensHeader = definition.lensLabel.charAt(0).toUpperCase() + definition.lensLabel.slice(1);

    const rows: RankedRow[] = result.rows.map((row) => {
        const rawKey = row.keys[definition.primaryField] ?? '—';
        const metricValue = row.metrics[metric];
        return {
            value: definition.primaryField === 'user_id' ? humanUser(rawKey) : rawKey,
            rawKey,
            metricValue,
            users: row.metrics.users,
            confidence: row.metrics.avgConfidence,
            share: share(metricValue, total),
            widthPct: peakShareFill(metricValue, max, 2),
        };
    });

    const primary: RankedListPrimary<RankedRow> = {
        key: 'value',
        header: lensHeader,
        label: (row) => row.value,
        fill: (row) => row.widthPct,
        share: (row) => formatPercent(row.share),
        sort: { get: (row) => row.value, initialDir: 'asc' },
    };

    const columns: RankedListColumn<RankedRow>[] = [
        { key: 'metricValue', header: metricLabel(metric), width: METRIC_WIDTH, render: (row) => formatCount(row.metricValue), sort: { get: (row) => row.metricValue, initialDir: 'desc' } },
    ];
    if (showPeople) {
        columns.push({ key: 'users', header: 'Users', width: REACH_WIDTH, render: (row) => formatCount(row.users), sort: { get: (row) => row.users, initialDir: 'desc' }, visibleFrom: 'sm', size: 'xs' });
    }
    if (showConfidence) {
        columns.push({ key: 'confidence', header: 'Conf.', width: REACH_WIDTH, render: (row) => formatPercent(row.confidence), sort: { get: (row) => row.confidence, initialDir: 'desc' }, visibleFrom: 'sm', size: 'xs' });
    }

    return (
        <RankedList
            rows={rows}
            primary={primary}
            columns={columns}
            rowKey={(row) => row.rawKey}
            accent={definition.accent}
            initialSort={{ key: 'metricValue', dir: 'desc' }}
            hrefFor={(row) => sliceToPath(basePath, definition.id, { primary: row.rawKey })}
            unitPlural={definition.lensPlural}
        />
    );
}
